<template>
  <div style="padding: 5px;">
    <h3>Files in {{ currentDir }}</h3>
    <div v-if="loading">
      <Spinner v-if="loading" height="50px"></Spinner>
      Files: {{ fileCount }}, Directories: {{ dirCount }}, Size: {{ roundFileBytes(fileSizeSum) }}<br><br>
    </div>
    <div v-else>
      Files: {{ fileCount }}, Directories: {{ dirCount }}, Size: {{ roundFileBytes(fileSizeSum) }}<br><br>
      <button type="button" style="width: 120px; margin-right: 5px;" class="btn btn-danger" @click="clearFiles(false)">Clear files</button>
      <button type="button" style="width: 120px; margin-right: 5px;" class="btn btn-danger" @click="clearFiles(true)">Delete all</button>
      <button type="button" style="width: 120px; margin-right: 5px;" class="btn btn-primary" @click="listFiles">Refresh</button>
      <button type="button" style="width: 120px; margin-right: 5px;" class="btn btn-primary" @click="previewAll">Preview
        all</button>
      <button v-if="currentDir != '/'" type="button" style="width: 120px; margin-right: 5px;" class="btn btn-success" @click="enterDir('..')">Back</button>
      <br><br>
      <table class="table table-striped">
        <thead>
          <tr height=20px>
            <td width=300px><b>Name</b></td>
            <td width=100px><b>Type</b></td>
            <td width=200px><b>Size</b></td>
            <td width=200px><b>Last modified</b></td>
            <td width=240px><b>Operations</b></td>
            <td width=600px><b>Data</b></td>
          </tr>
        </thead>
        <tbody>
          <CacheTableRow v-for="file in files" :key="file.name" :file="file" :currentDirHandle="currentDirHandle" :previewAllFiles="previewAllFiles" @enterdir="enterDir" @deletefile="deleteFile">
          </CacheTableRow>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script lang="ts">
import BaseComponent from '@/components/Base.vue';
import type { Row } from '@/ts/types';
import FileSaver from 'file-saver';
import { defineComponent } from "vue";

export default defineComponent({
  extends: BaseComponent,
  data() {
    const currentDirHandle: FileSystemDirectoryHandle = undefined;
    const opfsRoot: FileSystemDirectoryHandle = null;

    return {
      files: [] as Row[],
      loading: false,
      opfsRoot,
      currentDir: "/",
      currentDirHandle,
      dirCount: 0,
      fileCount: 0,
      fileSizeSum: 0,
      previewAllFiles: false,
    };
  },
  async created() {
    this.opfsRoot = await navigator.storage.getDirectory();
    this.currentDirHandle = this.opfsRoot;
  },
  mounted() {
    this.waitForElement();
  },
  methods: {
    async downloadFile(row: Row) {
      const fileHandle = await this.currentDirHandle.getFileHandle(row.name, { create: false });
      const file = await fileHandle.getFile();
      FileSaver.saveAs(file, row.name);
    },
    async previewAll() {
      this.previewAllFiles = true;
    },
    sortFiles(a: Row, b: Row) {
      if (a.name == '..') {
        return -1;
      }
      if (b.name == '..') {
        return 1;
      }
      if ((a.type == 'DIR') && (b.type == 'FILE')) {
        return -1;
      }
      if ((b.type == 'DIR') && (a.type == 'FILE')) {
        return 1;
      }
      if (a.name < b.name) {
        return -1;
      } else {
        return 1;
      }
    },
    async listFiles() {
      this.loading = true;
      this.previewAllFiles = false;
      this.files = [];
      const entries = this.currentDirHandle.values();
      this.dirCount = 0;
      this.fileCount = 0;
      this.fileSizeSum = 0;
      if (this.currentDir != "/") {
        const row = { 'name': '..', 'type': 'DIR', 'size': 0, 'human_size': '', 'data': '', 'lastModifiedDate': '', 'preview': '', 'human_date': '' };
        this.files.push(row);
      }
      for await (const entry of entries) {
        if (entry.kind == 'file') {
          const fileHandle = await this.currentDirHandle.getFileHandle(entry.name, { create: false });
          const file = await fileHandle.getFile();
          let text = '';
          if (file.name.endsWith('txt')) {
            text = await file.text();
          }
          const row = { 'name': file.name, 'type': 'FILE', 'size': file.size, 'human_size': this.roundFileBytes(file.size), 'data': text.substring(0, 200), 'lastModifiedDate': file.lastModified, 'preview': '', 'human_date': this.formatDateSecondsEpoch(file.lastModified) };
          this.files.push(row);
          this.fileCount += 1;
          this.fileSizeSum += file.size;
        } else {
          const row = { 'name': entry.name, 'type': 'DIR', 'size': 0, 'human_size': '', 'data': '', 'lastModifiedDate': '', 'preview': '', 'human_date': '' };
          this.files.push(row);
          this.dirCount += 1;
        }
      }
      this.files.sort(this.sortFiles);
      this.loading = false;
    },
    async clearFiles(includeDirs: boolean) {
      let message = 'Are you sure to clear all files in: ' + this.currentDir + '?';
      if (includeDirs) {
        message = 'Are tou sure to delete all files and directories in: ' + this.currentDir + '?';
      }
      const areYouSure = window.confirm(message);
      if (areYouSure) {
        this.loading = true;
        const entries = await this.currentDirHandle.values();
        for await (const entry of entries) {
          if (entry.kind == 'file') {
            const fileHandle = await this.currentDirHandle.getFileHandle(entry.name, { create: false });
            await fileHandle.remove();
          } else {
            if (includeDirs) {
              const fileHandle = await this.currentDirHandle.getDirectoryHandle(entry.name, { create: false });
              await fileHandle.remove({ recursive: true });
            }
          }
        }
        this.loading = false;
        this.listFiles();
      }
    },
    async enterDir(dirname: string) {
      if (dirname == '..') {
        this.currentDir = "/";
      } else {
        this.currentDir = this.currentDir + dirname;
      }
      if (this.currentDir == "/") {
        this.currentDirHandle = this.opfsRoot;
      } else {
        this.currentDirHandle = await this.opfsRoot.getDirectoryHandle(dirname, { create: false });
      }
      this.listFiles();
    },
    deleteFile(file: Row) {
      const index = this.files.findIndex(f => f.name === file.name);
      if (index !== -1) {
        this.files.splice(index, 1);
        if (file.type == 'FILE') {
          this.fileCount -= 1;
          this.fileSizeSum -= file.size;
        } else {
          this.dirCount -= 1;
        }
      }
    },
    waitForElement() {
      if (this.opfsRoot) {
        this.listFiles();
      } else {
        setTimeout(this.waitForElement, 100);
      }
    }
  }
})

</script>